// ink-component
import React, { RefObject, SelectHTMLAttributes } from 'react';
import { ThemeConsumer } from 'styled-components';

import CoreSelect from '../_common/CoreSelect/CoreSelect';
import TableSelect from './components/TableSelect/TableSelect';

import { ControllerProps } from '../_common/Controller/Controller';

export type NewSelectProps = Omit<ControllerProps, 'render' | 'theme'> &
  SelectHTMLAttributes<HTMLSelectElement> & {
    /** Select width */
    width?: 'large' | 'half' | 'quarter' | 'full';
    /** Modifier for invalid state */
    invalid?: boolean;
    /** React ref object to be forwarded to the internal node */
    externalRef?: RefObject<HTMLSelectElement>;
    /** Allows screen readers to describe the content. The correct use of Field excludes the need for aria-label */
    'aria-label'?: string;
    /** Marks the element with a given ID to make it easier to select in tests */
    'data-testid'?: string | number;
    /** Unique name for analytics purposes. By default, components are not tracked, unless this is set */
    'data-track-name'?: string;
    /** Custom payload to be sent to analytics provider */
    'data-track-payload'?: any;
    /** List of tracked events in the analytics integration */
    'data-track-events'?: string[];
  };

/**
 * @purpose Select one answer among three or more predefined options
 * @categories form
 * @aka select
 * @HTMLType HTMLSelectElement
 */
function NewSelect(props: NewSelectProps) {
  const { className, style, ...otherProps } = props;
  return (
    <ThemeConsumer>
      {cellTheme => {
        if (cellTheme && cellTheme.cellPreset === 'editable') {
          return <TableSelect {...otherProps} />;
        }
        return <CoreSelect {...otherProps} />;
      }}
    </ThemeConsumer>
  );
}

NewSelect.Option = CoreSelect.Option;
NewSelect.Group = CoreSelect.Group;

NewSelect.defaultProps = {
  width: 'large',
  invalid: false,
  'data-track-events': ['change'],
};

NewSelect.displayName = 'NewSelect';

export default NewSelect;
